/**
 * The names held in a pasted blob, in the order they were pasted.
 *
 * A customer adding rows in bulk already keeps the list somewhere: a spreadsheet column, which arrives
 * newline-delimited (`\r\n` out of Sheets and Excel), or an inline list written with commas, semicolons
 * or tabs. All of them separate here.
 *
 * A plain space is deliberately **not** a separator. Splitting on any whitespace looks right for a
 * domain whose names cannot contain spaces — until someone pastes "ad unit 1", which then arrives as
 * three rows that each pass validation instead of one row that visibly fails it. The paste should say
 * what was pasted; the name rules are what reject it, and they can only do that if the name reaches
 * them whole.
 *
 * Blank entries are dropped rather than kept as empty rows: a spreadsheet column ends on a trailing
 * newline, and it must not leave a row the user has to delete before the form will submit.
 *
 * Duplicates within one paste are dropped too, case-insensitively on the normalized name. They could
 * not all be created anyway — the names are unique per scope or per env — so keeping them would only
 * produce rows whose one purpose is to be rejected and removed by hand.
 *
 * `normalize` is the caller's own idea of the name it is collecting, so a domain that trims differently
 * (or not at all) stays in charge of that while sharing the splitting and the de-duplication.
 */
export const parsePastedNames = (text: string, normalize: (name: string) => string = (name) => name.trim()): string[] => {
  const seen = new Set<string>()
  return text
    .split(/[\n\r,;\t]+/)
    .map(normalize)
    .filter((name) => {
      if (!name) {
        return false
      }
      const key = name.toLocaleLowerCase()
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      return true
    })
}
